/*
 * Repository Traceability:
 * - Admin-only "Tenant Settings": consolidates per-tenant configuration used across Damage
 *   Intelligence — report branding (company name, branch name, logo) used on exported Trip
 *   Inspection PDFs, and the Trip Inspection branch policy (require a full 5-angle walkaround).
 *   Backed by GET/PUT /tenant/branding and GET/PUT /tenant/policy (di.configuration.manage).
 */
import React, { useEffect, useState } from "react";
import { Building2, ImagePlus, Loader2, Check, ShieldCheck, Settings2, Trash2, FileText } from "lucide-react";
import { api, envelopeError } from "../lib/api";
import { useAuth } from "../lib/auth-context";
import { T } from "../constants/testIds";

async function fileToLogoDataUrl(file) {
  const dataUrl = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
  const img = await new Promise((res, rej) => { const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl; });
  const maxDim = 320;
  let { width, height } = img;
  if (Math.max(width, height) > maxDim) { const s = maxDim / Math.max(width, height); width = Math.round(width * s); height = Math.round(height * s); }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/png");
}

export default function TenantSettings() {
  const { has, principal } = useAuth();
  const isAdmin = has("di.configuration.manage");

  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState("");
  const [branchName, setBranchName] = useState("");
  const [logoDataUrl, setLogoDataUrl] = useState(null);
  const [requireFull, setRequireFull] = useState(false);
  const [autoBlur, setAutoBlur] = useState(false);
  const [savingBranding, setSavingBranding] = useState(false);
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    Promise.all([api.get("/tenant/branding"), api.get("/tenant/policy")])
      .then(([b, p]) => {
        if (!active) return;
        const bd = b.data?.data || {}; const pd = p.data?.data || {};
        setCompanyName(bd.companyName || ""); setBranchName(bd.branchName || ""); setLogoDataUrl(bd.logoDataUrl || null);
        setRequireFull(!!pd.requireFullWalkaround);
        setAutoBlur(!!pd.autoBlurUploads);
      })
      .catch((err) => setError(envelopeError(err, "Could not load tenant settings.")))
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, []);

  const flash = (m) => { setSavedMsg(m); setTimeout(() => setSavedMsg(""), 2500); };

  const onLogoPick = async (file) => {
    setError("");
    try { setLogoDataUrl(await fileToLogoDataUrl(file)); }
    catch { setError("Could not read that image. Please try a different file."); }
  };

  const saveBranding = async () => {
    setSavingBranding(true); setError("");
    try {
      await api.put("/tenant/branding", { companyName, branchName, logoDataUrl });
      flash("Branding saved.");
    } catch (err) { setError(envelopeError(err, "Could not save branding.")); }
    finally { setSavingBranding(false); }
  };

  const savePolicy = async () => {
    setSavingPolicy(true); setError("");
    try {
      await api.put("/tenant/policy", { requireFullWalkaround: requireFull, autoBlurUploads: autoBlur });
      flash("Policy saved.");
    } catch (err) { setError(envelopeError(err, "Could not save policy.")); }
    finally { setSavingPolicy(false); }
  };

  if (!isAdmin) {
    return (
      <div data-testid={T.tenantSettingsRoot} className="px-8 py-8 max-w-3xl">
        <h1 className="text-2xl font-semibold text-white flex items-center gap-3"><Settings2 className="size-6 text-steel-300" /> Tenant Settings</h1>
        <p className="text-sm text-amber400 mt-4">You need administrator permission (di.configuration.manage) to manage tenant settings.</p>
      </div>
    );
  }

  return (
    <div data-testid={T.tenantSettingsRoot} className="px-8 py-8 max-w-3xl">
      <div className="text-[11px] uppercase tracking-[0.22em] text-steel-400">Damage Intelligence</div>
      <h1 className="text-2xl font-semibold text-white mt-1 flex items-center gap-3"><Settings2 className="size-6 text-steel-300" /> Tenant Settings</h1>
      <p className="text-sm text-steel-300 mt-2">Per-tenant configuration for <span className="font-mono text-steel-200">{principal?.tenantId}</span>. Applies to all staff in this tenant.</p>

      {error && <div data-testid="tenant-settings-error" className="mt-4 text-sm text-signal-soft bg-signal/10 border border-signal/30 rounded-md px-3 py-2">{error}</div>}
      {savedMsg && <div data-testid="tenant-settings-saved" className="mt-4 text-sm text-emerald400 bg-emerald-400/10 border border-emerald400/30 rounded-md px-3 py-2">{savedMsg}</div>}

      {loading ? (
        <div className="mt-8 flex items-center gap-2 text-steel-400 text-sm"><Loader2 className="size-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="mt-6 space-y-6">
          {/* Branding */}
          <section className="rounded-lg border border-ink-700/70 bg-ink-900/60 p-5">
            <div className="flex items-center gap-2 mb-4"><Building2 className="size-4 text-steel-300" /><span className="text-sm font-medium text-white">Report branding</span><span className="text-[11px] text-steel-500">printed on exported PDFs</span></div>
            <div className="flex flex-col sm:flex-row gap-6">
              <div className="w-40 shrink-0">
                <div className="text-[11px] uppercase tracking-wider text-steel-400 mb-2">Logo</div>
                <label className="block w-40 h-28 rounded-lg border-2 border-dashed border-ink-700 hover:border-signal/50 bg-ink-950 overflow-hidden cursor-pointer grid place-items-center transition-colors">
                  {logoDataUrl ? <img src={logoDataUrl} alt="Logo" className="w-full h-full object-contain bg-white" />
                    : <span className="flex flex-col items-center gap-1 text-steel-400 text-xs"><ImagePlus className="size-6" /> Upload logo</span>}
                  <input data-testid="tenant-logo-input" type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && onLogoPick(e.target.files[0])} />
                </label>
                {logoDataUrl && (
                  <button data-testid="tenant-logo-remove" onClick={() => setLogoDataUrl(null)} className="mt-2 text-[11px] text-steel-400 hover:text-signal-soft flex items-center gap-1"><Trash2 className="size-3" /> Remove logo</button>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-steel-400">Company name</label>
                  <input data-testid="tenant-company-input" value={companyName} onChange={(e) => setCompanyName(e.target.value)} placeholder="e.g. Riyadah Technology"
                    className="mt-1 w-full rounded-md bg-ink-900/70 border border-ink-700 px-3 py-2 text-sm text-steel-100 placeholder:text-steel-500 focus:outline-none focus:border-signal/50" />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-wider text-steel-400">Branch name</label>
                  <input data-testid="tenant-branch-input" value={branchName} onChange={(e) => setBranchName(e.target.value)} placeholder="e.g. Doha — Main Branch"
                    className="mt-1 w-full rounded-md bg-ink-900/70 border border-ink-700 px-3 py-2 text-sm text-steel-100 placeholder:text-steel-500 focus:outline-none focus:border-signal/50" />
                </div>
                <button data-testid="tenant-branding-save" onClick={saveBranding} disabled={savingBranding}
                  className="px-4 py-2 rounded-md text-sm font-medium bg-signal hover:bg-signal/90 disabled:opacity-50 text-white flex items-center gap-2">
                  {savingBranding ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />} Save branding
                </button>
              </div>
            </div>

            {/* Live PDF header preview */}
            <div className="mt-6">
              <div className="flex items-center gap-2 mb-2"><FileText className="size-3.5 text-steel-400" /><span className="text-[11px] uppercase tracking-wider text-steel-400">PDF preview</span><span className="text-[11px] text-steel-500">how exported reports will look</span></div>
              <div data-testid="tenant-pdf-preview" className="rounded-md overflow-hidden border border-ink-700 bg-white shadow-lg max-w-md">
                <div className="px-5 pt-5 pb-4" style={{ fontFamily: "Helvetica, Arial, sans-serif", color: "#222" }}>
                  <div className="flex items-center gap-3 pb-3" style={{ borderBottom: "2px solid #e0e0e0" }}>
                    {logoDataUrl ? <img src={logoDataUrl} alt="Logo" data-testid="tenant-pdf-preview-logo" style={{ height: 40, width: "auto" }} />
                      : <div className="grid place-items-center text-[9px] text-[#bbb] border border-dashed border-[#ddd]" style={{ height: 40, width: 64 }}>logo</div>}
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#111" }} data-testid="tenant-pdf-preview-company">{companyName || "Your company name"}</div>
                      {(branchName || true) && <div style={{ fontSize: 10, color: "#777" }} data-testid="tenant-pdf-preview-branch">{branchName || "Your branch name"}</div>}
                    </div>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: "#111", marginTop: 12 }}>Trip Inspection Report</div>
                  <div style={{ fontSize: 8, color: "#999", marginTop: 2 }}>{new Date().toLocaleDateString()} · advisory only</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 18px", marginTop: 8, fontSize: 9 }}>
                    <div><span style={{ color: "#777", fontWeight: 700 }}>Customer:</span> <span style={{ color: "#222" }}>Sample Customer</span></div>
                    <div><span style={{ color: "#777", fontWeight: 700 }}>Vehicle plate:</span> <span style={{ color: "#222" }}>ABC-1234</span></div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#b45309", marginTop: 10 }}>New damage found · 2 issues</div>
                  <div style={{ height: 1, background: "#eee", marginTop: 10 }} />
                  <div style={{ fontSize: 9, color: "#aaa", marginTop: 8 }}>Sample preview — actual report content is generated per inspection.</div>
                </div>
              </div>
            </div>
          </section>

          {/* Policy */}
          <section className="rounded-lg border border-ink-700/70 bg-ink-900/60 p-5">
            <div className="flex items-center gap-2 mb-4"><ShieldCheck className="size-4 text-steel-300" /><span className="text-sm font-medium text-white">Trip Inspection policy</span></div>
            <label className="flex items-center gap-2 text-sm text-steel-300 cursor-pointer w-fit" data-testid="tenant-policy-toggle">
              <input type="checkbox" checked={requireFull} onChange={(e) => setRequireFull(e.target.checked)} className="size-4 accent-signal" />
              Require a full 5-angle walkaround for every inspection
            </label>
            <p className="text-[12px] text-steel-400 mt-2">When enabled, staff must capture all 5 exterior angles (Front, Rear, Left, Right, Roof) before they can run an analysis — the walkaround gate is pre-enabled and locked.</p>
            <label className="flex items-center gap-2 text-sm text-steel-300 cursor-pointer w-fit mt-4" data-testid="tenant-autoblur-toggle">
              <input type="checkbox" checked={autoBlur} onChange={(e) => setAutoBlur(e.target.checked)} className="size-4 accent-signal" />
              Auto-blur faces &amp; bystander plates on stored evidence (PDPL/GDPR)
            </label>
            <p className="text-[12px] text-steel-400 mt-2">Every image saved to inspection evidence is scanned and any human faces or background-vehicle plates are blurred in place. The subject vehicle's own plate stays visible for OCR and vehicle linking.</p>
            <button data-testid="tenant-policy-save" onClick={savePolicy} disabled={savingPolicy}
              className="mt-3 px-4 py-2 rounded-md text-sm font-medium bg-signal hover:bg-signal/90 disabled:opacity-50 text-white flex items-center gap-2">
              {savingPolicy ? <Loader2 className="size-4 animate-spin" /> : <Check className="size-4" />} Save policy
            </button>
          </section>

          <p className="text-[11px] text-steel-500">These settings can also be provisioned by the CROMS / Maintenance system via the tenant API.</p>
        </div>
      )}
    </div>
  );
}
